import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner, usePageNavigation } from '@shared/components';
import { Button } from '@components/ui/button/Button';
import { ConfigurationPageHeader } from '@modules/configuration/components/ConfigurationPageHeader';
import {
  configurationPageClass,
  configurationPageContentClass,
} from '@modules/configuration/components/configurationLayout';
import { useIntegrations } from '../../hooks';
import { IntegrationCard } from '../display/IntegrationCard';
import { IntegrationsEmptyState } from '../display/IntegrationsEmptyState';

export const Main = () => {
  const { t } = useTranslation('integrationsModule');
  const { data, isLoading, error, refetch } = useIntegrations();

  const breadcrumbs = useMemo(() => [{ label: t('pageTitle') }], [t]);

  usePageNavigation({ breadcrumbs });

  if (isLoading) {
    return (
      <div className={`${configurationPageClass} items-center justify-center py-16`}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={configurationPageClass}>
        <ConfigurationPageHeader title={t('pageTitle')} description={t('pageDescription')} />
        <div
          className={`${configurationPageContentClass} flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-12 text-center`}
        >
          <p className="text-sm font-medium text-foreground">{t('error.title')}</p>
          <p className="mt-2 text-sm text-muted-foreground">{(error as Error).message}</p>
          <Button variant="outline" className="mt-4" onClick={() => refetch()}>
            {t('error.retry')}
          </Button>
        </div>
      </div>
    );
  }

  const providers = data?.data ?? [];

  return (
    <div className={`${configurationPageClass} pb-6`} data-testid="integrations-view">
      <ConfigurationPageHeader title={t('pageTitle')} description={t('pageDescription')} />

      <div className={`${configurationPageContentClass} overflow-y-auto`}>
        {providers.length === 0 ? (
          <IntegrationsEmptyState />
        ) : (
          <div className="space-y-3">
            {providers.map((provider) => (
              <IntegrationCard key={provider.name} provider={provider} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
